import React, { useState } from 'react';
import { Search, Package, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from '../hooks/use-toast';
import { shipmentAPI } from '../services/api';
import { statusColors } from '../data/constants';
import { useNavigate } from 'react-router-dom';

const HistoryPage = () => {
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleSearch = async () => {
    if (!phone) {
      toast({
        title: "Enter Phone Number",
        description: "Please enter your phone number to view shipments.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const data = await shipmentAPI.getShipmentsByPhone(phone);
      setShipments(data);
      if (data.length === 0) {
        toast({
          title: "No Shipments Found",
          description: "No shipments found for this phone number.",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch shipments. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#D62828] text-white py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold mb-4">My Shipments</h1>
          <p className="text-gray-100">View all your bookings</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Search */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="flex gap-4">
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Enter your phone number (09XXXXXXXXX)"
              className="flex-1 h-12 text-base"
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Button
              onClick={handleSearch}
              disabled={loading}
              className="bg-[#D62828] hover:bg-[#B01F1F] text-white px-8 h-12"
            >
              <Search className="w-5 h-5 mr-2" />
              {loading ? 'Searching...' : 'Search'}
            </Button>
          </div>
        </div>

        {/* Shipments List */}
        {shipments.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Found {shipments.length} shipment{shipments.length !== 1 ? 's' : ''}
            </h2>
            {shipments.map((shipment) => (
              <div
                key={shipment.tracking_number}
                className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(`/track?number=${shipment.tracking_number}`)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{shipment.tracking_number}</h3>
                    <p className="text-sm text-gray-600">
                      {new Date(shipment.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${statusColors[shipment.status]}`}>
                    {shipment.status}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-start gap-2">
                    <Package className="w-4 h-4 text-[#D62828] mt-1" />
                    <div>
                      <p className="text-xs text-gray-600">Service Type</p>
                      <p className="font-semibold text-sm">{shipment.service_type}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Package className="w-4 h-4 text-[#D62828] mt-1" />
                    <div>
                      <p className="text-xs text-gray-600">Travel Mode</p>
                      <p className="font-semibold text-sm">{shipment.travel_mode}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Package className="w-4 h-4 text-[#D62828] mt-1" />
                    <div>
                      <p className="text-xs text-gray-600">Size & Weight</p>
                      <p className="font-semibold text-sm">{shipment.length}x{shipment.width}x{shipment.height} cm, {shipment.weight} kg</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && shipments.length === 0 && (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Shipments Yet</h3>
            <p className="text-gray-600 mb-6">Enter your phone number to view your shipments</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoryPage;
